import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Save, 
  X, 
  Hash, 
  Building, 
  Star,
  AlertCircle
} from 'lucide-react';
import { productsService } from '../services/productsService';
import { suppliersService } from '../services/suppliersService';

const MultiSKUManager = ({ productId, onSkusUpdate }) => {
  const [skus, setSkus] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingSku, setEditingSku] = useState(null);
  const [formData, setFormData] = useState({
    supplier_id: '',
    sku: '',
    supplier_part_number: '',
    description: '',
    is_primary: false
  });

  // Cargar SKUs del producto
  const loadSkus = async () => {
    try {
      setLoading(true);
      const response = await productsService.getProductSkus(productId);
      if (response.success) {
        setSkus(response.data);
      }
    } catch (error) {
      console.error('Error cargando SKUs:', error);
    } finally {
      setLoading(false);
    }
  };

  // Cargar proveedores
  const loadSuppliers = async () => {
    try {
      const response = await suppliersService.getAll();
      // El servicio devuelve { data: [...] }
      setSuppliers(response.data || []);
    } catch (error) {
      console.error('Error cargando proveedores:', error);
      setSuppliers([]);
    }
  };

  useEffect(() => {
    if (productId) {
      loadSkus();
      loadSuppliers();
    }
  }, [productId]);

  // Limpiar formulario
  const resetForm = () => {
    setFormData({
      supplier_id: '',
      sku: '',
      supplier_part_number: '',
      description: '',
      is_primary: false
    });
    setEditingSku(null);
  };

  // Manejar envío del formulario
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.sku.trim()) {
      alert('El SKU es requerido');
      return;
    }

    try {
      let response;
      
      if (editingSku) {
        // Actualizar SKU existente
        response = await productsService.updateProductSku(productId, editingSku.id, formData);
      } else {
        // Crear nuevo SKU
        response = await productsService.addProductSku(productId, formData);
      }

      if (response.success) {
        await loadSkus();
        setShowAddModal(false);
        resetForm();
        if (onSkusUpdate) onSkusUpdate();
      } else {
        alert(response.error || 'Error al guardar SKU');
      }
    } catch (error) {
      console.error('Error guardando SKU:', error);
      alert('Error al guardar SKU');
    }
  };

  // Eliminar SKU
  const handleDelete = async (skuId) => {
    if (!confirm('¿Estás seguro de que quieres eliminar este SKU?')) {
      return;
    }

    try {
      const response = await productsService.deleteProductSku(productId, skuId);
      if (response.success) {
        await loadSkus();
        if (onSkusUpdate) onSkusUpdate();
      } else {
        alert(response.error || 'Error al eliminar SKU');
      }
    } catch (error) {
      console.error('Error eliminando SKU:', error);
      alert('Error al eliminar SKU');
    }
  };

  // Iniciar edición
  const startEdit = (sku) => {
    setEditingSku(sku);
    setFormData({
      supplier_id: sku.supplier_id || '',
      sku: sku.sku,
      supplier_part_number: sku.supplier_part_number || '',
      description: sku.description || '',
      is_primary: sku.is_primary
    });
    setShowAddModal(true);
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-xl p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            <div className="h-12 bg-gray-200 rounded"></div>
            <div className="h-12 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-xl p-6">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
          <Hash className="w-6 h-6 text-blue-600" />
          SKUs por Proveedor
        </h3>
        <button
          onClick={() => {
            resetForm();
            setShowAddModal(true);
          }}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Agregar SKU
        </button>
      </div>

      {skus.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <Hash className="w-12 h-12 mx-auto mb-4 text-gray-300" />
          <p>No hay SKUs adicionales registrados</p>
          <p className="text-sm">Agrega códigos de diferentes proveedores</p>
        </div>
      ) : (
        <div className="space-y-3">
          {skus.map((sku) => (
            <motion.div
              key={sku.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-blue-300 transition-colors"
            >
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <span className="font-mono text-lg bg-gray-100 px-3 py-1 rounded border-l-4 border-blue-500">
                    {sku.sku}
                  </span>
                  {sku.is_primary && (
                    <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs flex items-center gap-1">
                      <Star className="w-3 h-3" />
                      Principal
                    </span>
                  )}
                </div>
                
                <div className="flex items-center gap-4 text-sm text-gray-600">
                  <div className="flex items-center gap-1">
                    <Building className="w-4 h-4" />
                    <span>{sku.supplier_name || 'Sin proveedor'}</span>
                  </div>
                  {sku.supplier_part_number && (
                    <span className="bg-gray-50 px-2 py-1 rounded">
                      Parte: {sku.supplier_part_number}
                    </span>
                  )}
                </div>
                
                {sku.description && (
                  <p className="text-sm text-gray-500 mt-1">{sku.description}</p>
                )}
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => startEdit(sku)}
                  className="text-blue-600 hover:text-blue-800 p-2 rounded-lg hover:bg-blue-50 transition-colors"
                  title="Editar SKU"
                >
                  <Edit className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDelete(sku.id)}
                  className="text-red-600 hover:text-red-800 p-2 rounded-lg hover:bg-red-50 transition-colors"
                  title="Eliminar SKU"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Modal para agregar/editar SKU */}
      <AnimatePresence>
        {showAddModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowAddModal(false);
                resetForm();
              }
            }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-md"
            >
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-xl font-bold text-gray-800">
                    {editingSku ? 'Editar SKU' : 'Agregar Nuevo SKU'}
                  </h3>
                  <button
                    onClick={() => {
                      setShowAddModal(false);
                      resetForm();
                    }}
                    className="text-gray-400 hover:text-gray-600 p-1"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* SKU */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      SKU / Código *
                    </label>
                    <input
                      type="text"
                      value={formData.sku}
                      onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Ej: ABC-123456"
                      required
                    />
                  </div>

                  {/* Proveedor */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Proveedor
                    </label>
                    <select
                      value={formData.supplier_id}
                      onChange={(e) => setFormData({ ...formData, supplier_id: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Sin proveedor específico</option>
                      {suppliers.map((supplier) => (
                        <option key={supplier.id} value={supplier.id}>
                          {supplier.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Número de parte del proveedor */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Número de Parte del Proveedor
                    </label>
                    <input
                      type="text"
                      value={formData.supplier_part_number}
                      onChange={(e) => setFormData({ ...formData, supplier_part_number: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Número de parte interno del proveedor"
                    />
                  </div>

                  {/* Descripción */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Descripción
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      rows="2"
                      placeholder="Descripción adicional del SKU"
                    />
                  </div>

                  {/* SKU Principal */}
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="is_primary"
                      checked={formData.is_primary}
                      onChange={(e) => setFormData({ ...formData, is_primary: e.target.checked })}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <label htmlFor="is_primary" className="text-sm text-gray-700 flex items-center gap-1">
                      <Star className="w-4 h-4 text-yellow-500" />
                      Marcar como SKU principal
                    </label>
                  </div>

                  {formData.is_primary && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                      <div className="flex items-center gap-2">
                        <AlertCircle className="w-4 h-4 text-yellow-600" />
                        <span className="text-sm text-yellow-800">
                          Al marcar como principal, se desmarcará el SKU principal actual
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Botones */}
                  <div className="flex gap-3 pt-4">
                    <button
                      type="button"
                      onClick={() => {
                        setShowAddModal(false);
                        resetForm();
                      }}
                      className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center justify-center gap-2 transition-colors"
                    >
                      <Save className="w-4 h-4" />
                      {editingSku ? 'Actualizar' : 'Guardar'}
                    </button>
                  </div>
                </form>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MultiSKUManager;
